import {
  Button,
  ButtonMode,
  colors,
  DataLoadError,
  Divider,
  Icon,
  ICONS,
  Loader,
  TabData,
  Tabs,
  ToasterMode,
  Tooltip,
  useGlobalModalContext,
} from '@creditsights/styleguide-web';
import { ColumnDef, Row, Table as ReactTable } from '@tanstack/react-table';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import FiltersBar from '../../../../components/FiltersBar';
import { FiltersBarHandle } from '../../../../components/FiltersBar/FiltersBar';
import { MODAL_TYPES } from '../../../../components/GlobalModal/GlobalModal';
import ResultsFilter from '../../../../components/ResultsFilter';
import Table from '../../../../components/Table';
import { TableHandle } from '../../../../components/Table/Table';
import usePreviousValue from '../../../../hooks/common/usePreviousValue';
import useCheckDownloadDocketDocumentProgress from '../../../../hooks/dockets/useCheckDownloadDocketDocumentProgress';
import useFetchCaseDocuments, { CaseDocumentsQueryFilters } from '../../../../hooks/dockets/useFetchCaseDocuments';
import useInitiateDownloadDocketDocument, {
  UseInitiateDownloadDocketDocumentPayload,
} from '../../../../hooks/dockets/useInitiateDownloadDocketDocument';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import { useToast } from '../../../../hooks/toast/useToast';
import { DateFilterOption, Filters, KeywordFilterOption } from '../../../../models';
import { DocketCase, DocketCaseDocument } from '../../../../models/CompanyDockets';
import { DATE_FORMATS, formatDate } from '../../../../utils/dateUtils';
import {
  hasNoPermissionToDocketDocument,
  hasPermissionToRequestDocketDocument,
} from '../../../../utils/permissionsUtils';
import AttachmentLoader from '../AttachmentLoader';
import AttachmentSeeMore from '../AttachmentSeeMore';
import { ERROR_DOWNLOAD_DOCUMENT_MESSAGE } from '../CompanyDocketsTab.constants';
import styles from './DocketCaseDocumentsTable.module.scss';
import { CompanyPageParams } from '../../../../models/CompanyPageParams';
import { CompanyOutletContext } from '../../Company';
import { trackDocketDocumentDownload } from '../../../../utils/trackUsageUtils';

const cx = classNames.bind(styles);

enum eventType {
  requestDocketDocument = 'DOCKET_DOCUMENT_REQUEST',
  viewDocketDocument = 'DOCKET_DOCUMENT_VIEW',
}

type CustomTableState = {
  docketItemsDownloading: Record<number, boolean>;
};

const setDocketItemDownloadingState = (
  table: ReactTable<DocketCase>,
  docket: DocketCaseDocument,
  isDownloading: boolean
) => {
  table.setState((prev) => ({
    ...prev,
    custom: {
      ...(prev.custom as CustomTableState),
      docketItemsDownloading: {
        ...(prev.custom as CustomTableState).docketItemsDownloading,
        [docket.docketItemId]: isDownloading,
      },
    },
  }));
};

const isDocumentDisabled = (document: DocketCaseDocument) => !document.canAccess;
const isRowHovered = (row: Row<DocketCaseDocument>, table) => {
  const { hover } = table.getState();
  return hover === row.id;
};

const renderDocument = (row: Row<DocketCaseDocument>, table, onDownload, docketDocumentDownloadHandler) => {
  const { original: document } = row;
  const { link, description } = document;
  return link ? (
    <a
      className={cx(
        'action',
        { 'gray-out': isDocumentDisabled(document) && isRowHovered(row, table) },
        { 'is-expanded': row.getIsExpanded() }
      )}
      href={link}
      target="_blank"
      rel="noreferrer"
      onClick={() => docketDocumentDownloadHandler(document, eventType.viewDocketDocument)}
    >
      {description}
    </a>
  ) : (
    <Button
      className={cx(
        'action',
        { 'gray-out': isDocumentDisabled(document) && isRowHovered(row, table) },
        { 'is-expanded': row.getIsExpanded() }
      )}
      mode={ButtonMode.PLAIN}
      dataTestId="download-document"
      onClick={() => onDownload(document, table)}
    >
      {description}
    </Button>
  );
};

const renderSubscribe = (onLockedContentClick) => {
  return (
    <div className={cx('subscribe-container')} data-testid="subscribe">
      <button type="button" onClick={onLockedContentClick} />
      <div className={cx('subscribe')}>
        <span className={cx('label-container')}>
          <Icon name={ICONS.LockOutline} />
          <Divider vertical className={cx('divider')} />
          <span className={cx('label')} data-testid="label-text">
            Subscribe
          </span>
        </span>
      </div>
    </div>
  );
};

const Header = () => {
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Tooltip content={isHovered ? 'Document Available' : ''}>
        <div>
          <Icon name={ICONS.Checkmark} color={colors.primary800} width={14} height={10} dataTestId="checkmark" />
        </div>
      </Tooltip>
    </div>
  );
};

const getColumns = (
  onLockedContentClick,
  onDownload,
  userPermissions,
  docketDocumentDownloadHandler
): ColumnDef<DocketCaseDocument>[] => [
  {
    cell: ({ row, table }) => {
      const { original: document } = row;
      if (document.isAttachment) {
        return null;
      }
      return (
        <span className={cx({ 'gray-out': isDocumentDisabled(document) && isRowHovered(row, table) })}>
          {document.docketNumber}
        </span>
      );
    },
    header: 'Filing #',
    size: 62,
  },
  {
    id: 'checkmark',
    cell: ({ row, table }) => {
      const { original: document } = row;
      const { docketItemsDownloading } = table.getState().custom as CustomTableState;
      if (docketItemsDownloading[document.docketItemId]) {
        return (
          <Tooltip content="Retrieving document">
            <div>
              <Loader text={null} dataTestId="retrieving-document-loader" />
            </div>
          </Tooltip>
        );
      }
      if (!document.fileAvailable || hasNoPermissionToDocketDocument(userPermissions)) {
        return null;
      }
      return (
        <Tooltip content="Document available">
          <div className={cx({ 'gray-out': isDocumentDisabled(document) && isRowHovered(row, table) })}>
            <Icon name={ICONS.Checkmark} color={colors.primary800} width={14} height={10} dataTestId="checkmark" />
          </div>
        </Tooltip>
      );
    },
    header: () => <Header />,
    size: 32,
  },
  {
    cell: ({ row, table }) => {
      return renderDocument(row, table, onDownload, docketDocumentDownloadHandler);
    },
    header: 'Document Description',
  },
  {
    cell: ({ row, table }) => {
      const { original: document } = row;
      if (isRowHovered(row, table) && isDocumentDisabled(document)) {
        return renderSubscribe(onLockedContentClick);
      }
      return document.filedDate && !document.isAttachment ? <span>{formatDate(document.filedDate)}</span> : null;
    },
    header: 'Date Filed',
    size: 104,
  },
];

interface DocketCaseDocumentsTableProps {
  docketCase: DocketCase;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  pageSizes: number[];
  onPageSizeChange: (pageSize: number) => void;
}

const DocketCaseDocumentsTable = ({
  docketCase,
  page,
  setPage,
  pageSize,
  pageSizes,
  onPageSizeChange,
}: DocketCaseDocumentsTableProps): ReactElement | null => {
  const { data: userPermissions } = useFetchUserPermissions();
  const { showModal } = useGlobalModalContext();
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  const filtersRef = useRef<FiltersBarHandle | null>(null);
  const tableRef = useRef<TableHandle<DocketCaseDocument> | null>(null);
  const [isKeyFilingsTab, setIsKeyFilingsTab] = useState(true);

  const [filters, setFilters] = useState<CaseDocumentsQueryFilters>({});
  const { data, isError, refetch, isLoading, totalPages, totalDocuments } = useFetchCaseDocuments(
    docketCase.id,
    page,
    pageSize,
    isKeyFilingsTab,
    filters
  );
  const { mutateAsync: initiateDocumentDownload } = useInitiateDownloadDocketDocument();
  const { mutateAsync: checkDownloadProgress } = useCheckDownloadDocketDocumentProgress();
  const { showToast } = useToast();

  const tabData: TabData[] = [
    {
      label: 'Key Filings',
      active: isKeyFilingsTab,
      tooltipText: 'Excludes documents such as fee statements, certificates of mailing, and affidavits of service.',
      onClick: () => onTabChange(true),
    },
    {
      label: 'All Filings',
      active: !isKeyFilingsTab,
      onClick: () => onTabChange(false),
    },
  ];

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
    tableRef.current?.table.resetExpanded();
  }, [pageSize]);

  const onLockedContentClick = () => {
    showModal(MODAL_TYPES.DOCKET_LOCKED_CONTENT);
  };

  const { issuerId } = useParams() as CompanyPageParams;
  const { company } = useOutletContext<CompanyOutletContext>();
  const docketCaseRef = useRef(docketCase);

  useEffect(() => {
    docketCaseRef.current = docketCase;
  }, [docketCase]);

  const docketDocumentDownloadHandler = (document: DocketCaseDocument, eventType: string) => {
    const currentDocketCase = docketCaseRef.current;
    trackDocketDocumentDownload(issuerId, company?.displayName, document, currentDocketCase, eventType);
  };

  const onAfterDocumentDownloadInitiated = (
    payload: UseInitiateDownloadDocketDocumentPayload,
    requestId: string,
    table: ReactTable<DocketCase>
  ) => {
    checkDownloadProgress({
      ...payload,
      requestId,
      onSuccess: (response) => {
        window.open(response.permalink, '_blank', 'noreferrer');
      },
      onSettled: () => {
        setDocketItemDownloadingState(table, payload.docketCaseDocument, false);
        docketDocumentDownloadHandler(payload.docketCaseDocument, eventType.requestDocketDocument);
      },
    });
  };

  const isDocketDocumentDownloading = (docket: DocketCaseDocument, table: ReactTable<DocketCase>) => {
    const { docketItemsDownloading } = table.getState().custom as CustomTableState;
    return docketItemsDownloading[docket.docketItemId];
  };

  const onDownload = async (docket: DocketCaseDocument, table: ReactTable<DocketCase>) => {
    if (!isDocketDocumentDownloading(docket, table) && hasPermissionToRequestDocketDocument(userPermissions)) {
      try {
        setDocketItemDownloadingState(table, docket, true);
        const payload: UseInitiateDownloadDocketDocumentPayload = {
          docketCaseDocument: docket,
          page: pageRef.current,
          pageSize: pageSizeRef.current,
          filters,
        };
        await initiateDocumentDownload(payload, {
          onSuccess: ({ requestId }) => {
            onAfterDocumentDownloadInitiated(payload, requestId, table);
          },
          onError: () => {
            setDocketItemDownloadingState(table, docket, false);
          },
        });
      } catch (error) {
        showToast(ERROR_DOWNLOAD_DOCUMENT_MESSAGE, ToasterMode.ERROR);
      }
    }
  };

  const columns = useMemo(
    () => getColumns(onLockedContentClick, onDownload, userPermissions, docketDocumentDownloadHandler),
    []
  );

  const onPageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0 });
  };

  const onFilterChange = (selectedFilters: Filters) => {
    const newFilters: CaseDocumentsQueryFilters = {};
    if (selectedFilters.startDate) {
      newFilters.itemFiledFromDate = formatDate(selectedFilters.startDate, DATE_FORMATS.YEAR_MONTH_DAY);
    }
    if (selectedFilters.endDate) {
      newFilters.itemFiledToDate = formatDate(selectedFilters.endDate, DATE_FORMATS.YEAR_MONTH_DAY);
    }
    if (selectedFilters?.keyword?.trim()) {
      newFilters.keyword = selectedFilters.keyword.trim();
    }
    setFilters(newFilters);
    setPage(1);
    tableRef.current?.table.resetExpanded();
  };

  const onTabChange = (keyFilings: boolean) => {
    setIsKeyFilingsTab(keyFilings);
    setPage(1);
    tableRef.current?.table.resetExpanded();
  };

  const isLoadingError = !isLoading && isError;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('loader-body')}>
          <Loader position="center" />
        </div>
      );
    }
    if (isLoadingError) {
      return (
        <div className={cx('error-container')}>
          <DataLoadError onRetryClick={refetch} />
        </div>
      );
    }
    return (
      <Table
        data={data}
        columns={columns}
        expandable
        getRowCanExpand={({ original: document }) => document.hasAttachments}
        getSubRows={(originalRow) => (originalRow.seeMore ? originalRow.subRows : originalRow.subRows?.slice(0, 5))}
        renderSubComponent={(props) => (
          <AttachmentLoader page={page} pageSize={pageSize} filters={filters} keyFilings={isKeyFilingsTab} {...props} />
        )}
        renderLastExpandedComponent={(props) => (
          <AttachmentSeeMore
            page={page}
            pageSize={pageSize}
            filters={filters}
            keyFilings={isKeyFilingsTab}
            {...props}
          />
        )}
        paginated
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        dataTestId="dockets-table"
        initialState={{ custom: { docketItemsDownloading: {} } }}
        ref={tableRef}
      />
    );
  };

  const previousCaseId = usePreviousValue(docketCase.id);

  useEffect(() => {
    if (previousCaseId && docketCase.id !== previousCaseId) {
      filtersRef.current?.reset();
    }
  }, [docketCase.id, previousCaseId]);

  return (
    <div className={cx('body')}>
      {!isLoadingError && (
        <div>
          <div className={cx('header')}>
            <div className={cx('filters')}>
              <div className={cx('filters-text-container')}>
                <div className={cx('filings-headline')}>Filings</div>
                <div className={cx('filings-number')}>{!isLoadingError && !isLoading && totalDocuments}</div>
                <span className={cx('separator')} />
                <div className={cx('filter-text')}>Filter by:</div>
              </div>
              <div className={cx('filters-container')}>
                <FiltersBar
                  ref={filtersRef}
                  filterTypes={[KeywordFilterOption, DateFilterOption]}
                  onChange={onFilterChange}
                  disableMultiline
                />
              </div>
            </div>
            {!isEmpty(data) && (
              <div>
                <ResultsFilter values={pageSizes} onResultsCountChange={onPageSizeChange} selectedValue={pageSize} />
              </div>
            )}
          </div>
          <Tabs tabs={tabData} />
        </div>
      )}
      {renderContent()}
    </div>
  );
};

export default DocketCaseDocumentsTable;
